import * as React from 'react';
import { clamp, isEqual, isNil } from 'lodash';

import InputRange from 'react-input-range';

import VerticalSpacing from 'components/vertical-spacing/VerticalSpacing';
import RangeSelectorHeader from 'modules/discovery/creators/components/filters/range-selector-header/RangeSelectorTitle';

import './controlled-range-select.scss';

import type { DiscoveryFilterInstagramFollowers, DiscoveryFilterMinMax, DiscoveryFilterNumber } from 'types';
import type { Range } from 'react-input-range';

type Props = {
  title?: string,
  tooltip?: string,
  attribute?: string,
  data: DiscoveryFilterNumber | DiscoveryFilterInstagramFollowers,
  increment?: number,
  isDisabled?: boolean,
  snapPoints?: Array<DiscoveryFilterNumber>,
  titleValueTransformer?: (a: DiscoveryFilterMinMax) => string,
  onChangeComplete: (b: DiscoveryFilterMinMax, a?: string | null) => void
};

type State = {
  min: number,
  max: number
};

function getClosestIndex(data: Array<number>, valueToMatch: number): number {
  const reduced = data.reduce((acc, entry, index) => {
    if (Math.abs(entry - valueToMatch) < Math.abs(acc.closestValue - valueToMatch)) {
      return {
        closestIndex: index,
        closestValue: entry
      };
    }
    return acc;
  }, {
    closestIndex: -1,
    closestValue: Number.MIN_SAFE_INTEGER
  });

  return reduced.closestIndex;
}

class ControlledRangeSelect extends React.Component<Props, State> {
  state = {
    min: this.props.data.min,
    max: this.props.data.max
  };

  componentDidUpdate(prevProps: Props): void {
    const { data: newData } = this.props;

    if (!isEqual(prevProps.data, newData)) {
      // eslint-disable-next-line
      this.setState({
        min: newData.min,
        max: newData.max
      });
    }
  }

  handleChangeRange = (value: number | Range): void => {
    const { data, snapPoints } = this.props;

    if (isNil(value) || typeof value === 'number') {
      return;
    }

    let minToSet = value.min;
    let maxToSet = value.max;

    if (snapPoints) {
      let newMinIndex = getClosestIndex(snapPoints.map(entry => entry.min), value.min);
      let newMaxIndex = getClosestIndex(snapPoints.map(entry => entry.max), value.max);

      if (newMinIndex === -1) {
        newMinIndex = 0;
      }

      if (newMaxIndex === -1) {
        newMaxIndex = snapPoints.length - 1;
      }

      if (newMinIndex > newMaxIndex) {
        newMinIndex = newMaxIndex;
      }

      minToSet = snapPoints[newMinIndex].min;
      maxToSet = snapPoints[newMaxIndex].max;
    }

    this.setState({
      min: clamp(minToSet, data.from, data.to),
      max: clamp(maxToSet, data.from, data.to)
    });
  };

  handleChangeComplete = (value: number | Range): void => {
    const { onChangeComplete, data, attribute } = this.props;

    if (!onChangeComplete || typeof value === 'number') {
      return;
    }

    onChangeComplete(
      {
        min: clamp(value.min, data.from, data.to),
        max: clamp(value.max, data.from, data.to)
      },
      attribute
    );
  };

  render(): React.ReactNode {
    const {
      title,
      tooltip,
      titleValueTransformer,
      data: { from, to },
      increment,
      isDisabled
    } = this.props;

    const { min, max } = this.state;

    const value = {
      min,
      max
    };

    const headerValue = (titleValueTransformer)
      ? titleValueTransformer(value)
      : `${min} – ${max}`;

    const inputClassName = (isDisabled)
      ? 'controlled-range-select--disabled'
      : '';

    return (
      <div className={inputClassName}>
        {Boolean(title) && (
          <React.Fragment>
            <RangeSelectorHeader
              title={title || ''}
              tooltip={tooltip}
              value={headerValue}
            />
            <VerticalSpacing type="medium" />
          </React.Fragment>
        )}
        <div className="controlled-range-select__slider">
          <InputRange
            disabled={isDisabled}
            minValue={from}
            maxValue={to}
            step={increment}
            value={value}
            onChange={this.handleChangeRange}
            onChangeComplete={this.handleChangeComplete}
          />
        </div>
      </div>
    );
  }
}

export default ControlledRangeSelect;
